/*
 * WEEKLY CARDS ADMIN - CREATE/EDIT PAGE
 *
 * NON-NEGOTIABLE UX PHILOSOPHY:
 * 1. This page handles both creating NEW editions and editing EXISTING non-live editions
 * 2. Create mode: Auto-assigns current/next Monday + 3 card editors + [Save Draft] [Schedule] [Publish Now]
 * 3. Edit mode: Same editors + same actions (can re-schedule, publish, etc.)
 * 4. Schedule button opens modal with date/time picker
 * 5. All changes write to Supabase
 * 6. Artist Wisdom: image upload + quote + artist name
 * 7. Civic Moment: country selector (searchable, title = country name) + story + optional source (image from country)
 * 8. Spark a Thought: question + optional subtext
 */

import { useState, useEffect } from 'react';
import { Loader2, Upload, Calendar, Search, ChevronDown, ArrowLeft, MoreVertical, X } from 'lucide-react';
import AdminShell from '../components/AdminShell';
import BottomSheetAction from '../components/BottomSheetAction';
import Toast from '../components/Toast';
import CalendarPicker from '../components/CalendarPicker';
import CountryImageManager from '../components/CountryImageManager';
import { useApp } from '../lib/AppContext';
import {
  getWeekById,
  createWeek,
  getOrCreateWeek,
  upsertCard,
  publishWeekNow,
  scheduleWeek,
  getAllCountries,
  uploadWeeklyCardImage,
  deleteWeeklyCardImage,
  publishNow,
  WeekWithCards,
  Country,
  CardType,
} from '../lib/weeklyCardsPlanner';

interface EditorFormData {
  artist_wisdom: {
    content: string;
    attribution: string;
    image_url: string;
  };
  civic_moment: {
    headline: string;
    content: string;
    country_id: string;
    event_date: string;
    source_name: string;
    source_url: string;
    story_image_url: string;
  };
  spark_a_thought: {
    content: string;
    subtext: string;
  };
}

function formatWeekLabel(weekOf: string): string {
  const date = new Date(weekOf + 'T00:00:00');
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getMondayOfWeek(dateString: string): string {
  const date = new Date(dateString + 'T00:00:00');
  const dayOfWeek = date.getDay();
  const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const monday = new Date(date);
  monday.setDate(date.getDate() + diff);
  return monday.toISOString().split('T')[0];
}

export default function AdminWeeklyCardsCreateEdit() {
  const { navigateTo, navigationState } = useApp();
  const weekId = navigationState?.weekId;
  const isEditMode = Boolean(weekId);

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState<CardType | null>(null);
  const [isDraggingArtist, setIsDraggingArtist] = useState(false);
  const [isDraggingCivic, setIsDraggingCivic] = useState(false);
  const [weekData, setWeekData] = useState<WeekWithCards | null>(null);

  function getDefaultMonday(): string {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const daysUntilMonday = dayOfWeek === 0 ? 1 : dayOfWeek === 1 ? 0 : 8 - dayOfWeek;
    const targetMonday = new Date(today);
    targetMonday.setDate(today.getDate() + daysUntilMonday);
    return targetMonday.toISOString().split('T')[0];
  }

  const [newWeekOf, setNewWeekOf] = useState(getDefaultMonday());

  const [formData, setFormData] = useState<EditorFormData>({
    artist_wisdom: { content: '', attribution: '', image_url: '' },
    civic_moment: { headline: '', content: '', country_id: '', event_date: '', source_name: '', source_url: '', story_image_url: '' },
    spark_a_thought: { content: '', subtext: '' },
  });

  const [originalFormData, setOriginalFormData] = useState<EditorFormData>({
    artist_wisdom: { content: '', attribution: '', image_url: '' },
    civic_moment: { headline: '', content: '', country_id: '', event_date: '', source_name: '', source_url: '', story_image_url: '' },
    spark_a_thought: { content: '', subtext: '' },
  });

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);

  const [countries, setCountries] = useState<Country[]>([]);
  const [countrySearch, setCountrySearch] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('09:00');

  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  const [toast, setToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' }>({
    show: false,
    message: '',
    type: 'success',
  });

  useEffect(() => {
    loadData();
  }, [weekId]);

  async function loadData() {
    const allCountries = await getAllCountries();
    setCountries(allCountries);

    if (isEditMode && weekId) {
      setLoading(true);
      const week = await getWeekById(weekId);
      if (!week) {
        showToast('Edition not found', 'error');
        navigateTo('admin-weekly-cards');
        return;
      }
      setWeekData(week);
      loadWeekIntoForm(week);
      setLoading(false);
    }
  }

  function addCacheBuster(url: string | null): string {
    if (!url) return '';
    const cleanUrl = url.split('?')[0];
    return `${cleanUrl}?t=${Date.now()}`;
  }

  function loadWeekIntoForm(week: WeekWithCards) {
    const newFormData: EditorFormData = {
      artist_wisdom: { content: '', attribution: '', image_url: '' },
      civic_moment: { headline: '', content: '', country_id: '', event_date: '', source_name: '', source_url: '', story_image_url: '' },
      spark_a_thought: { content: '', subtext: '' },
    };

    week.cards.forEach(card => {
      if (card.card_type === 'artist_wisdom') {
        newFormData.artist_wisdom = {
          content: card.content,
          attribution: card.attribution || '',
          image_url: card.image_url ? addCacheBuster(card.image_url) : '',
        };
      } else if (card.card_type === 'civic_moment') {
        newFormData.civic_moment = {
          headline: card.headline || '',
          content: card.content,
          country_id: card.country_id || '',
          event_date: card.event_date || '',
          source_name: card.source_name || '',
          source_url: card.source_url || '',
          story_image_url: card.story_image_url ? addCacheBuster(card.story_image_url) : '',
        };
      } else if (card.card_type === 'spark_a_thought') {
        newFormData.spark_a_thought = {
          content: card.content,
          subtext: card.subtext || '',
        };
      }
    });

    setFormData(newFormData);
    setOriginalFormData(JSON.parse(JSON.stringify(newFormData)));
  }

  function stripCacheBuster(url: string): string {
    if (!url) return url;
    return url.split('?')[0];
  }

  // Detect if there are actual changes from the original state
  function detectChanges(): boolean {
    const normalizeValue = (val: any) => {
      if (val === null || val === undefined || val === '') return '';
      return val;
    };

    // Compare artist wisdom
    if (normalizeValue(formData.artist_wisdom.content) !== normalizeValue(originalFormData.artist_wisdom.content)) return true;
    if (normalizeValue(formData.artist_wisdom.attribution) !== normalizeValue(originalFormData.artist_wisdom.attribution)) return true;
    if (stripCacheBuster(normalizeValue(formData.artist_wisdom.image_url)) !== stripCacheBuster(normalizeValue(originalFormData.artist_wisdom.image_url))) return true;

    // Compare civic moment
    if (normalizeValue(formData.civic_moment.headline) !== normalizeValue(originalFormData.civic_moment.headline)) return true;
    if (normalizeValue(formData.civic_moment.content) !== normalizeValue(originalFormData.civic_moment.content)) return true;
    if (normalizeValue(formData.civic_moment.country_id) !== normalizeValue(originalFormData.civic_moment.country_id)) return true;
    if (normalizeValue(formData.civic_moment.event_date) !== normalizeValue(originalFormData.civic_moment.event_date)) return true;
    if (normalizeValue(formData.civic_moment.source_name) !== normalizeValue(originalFormData.civic_moment.source_name)) return true;
    if (normalizeValue(formData.civic_moment.source_url) !== normalizeValue(originalFormData.civic_moment.source_url)) return true;
    if (stripCacheBuster(normalizeValue(formData.civic_moment.story_image_url)) !== stripCacheBuster(normalizeValue(originalFormData.civic_moment.story_image_url))) return true;

    // Compare spark a thought
    if (normalizeValue(formData.spark_a_thought.content) !== normalizeValue(originalFormData.spark_a_thought.content)) return true;
    if (normalizeValue(formData.spark_a_thought.subtext) !== normalizeValue(originalFormData.spark_a_thought.subtext)) return true;

    return false;
  }

  // Update hasUnsavedChanges whenever form data changes
  useEffect(() => {
    if (loading) return;
    const hasChanges = detectChanges();
    setHasUnsavedChanges(hasChanges);
  }, [formData, originalFormData, loading]);

  function handleBack() {
    if (hasUnsavedChanges) {
      setPendingNavigation(() => () => navigateTo('admin-weekly-cards'));
      setShowUnsavedDialog(true);
      return;
    }
    navigateTo('admin-weekly-cards');
  }

  function handleDiscardChanges() {
    setShowUnsavedDialog(false);
    if (pendingNavigation) {
      pendingNavigation();
      setPendingNavigation(null);
    }
  }

  function handleContinueEditing() {
    setShowUnsavedDialog(false);
    setPendingNavigation(null);
  }

  async function saveCards(weekIdToSave: string) {
    const cardTypes: CardType[] = ['artist_wisdom', 'civic_moment', 'spark_a_thought'];

    for (const cardType of cardTypes) {
      const data = formData[cardType];
      if (cardType === 'artist_wisdom') {
        await upsertCard(weekIdToSave, {
          card_type: cardType,
          content: data.content,
          attribution: data.attribution,
          image_url: stripCacheBuster(data.image_url) || null,
        });
      } else if (cardType === 'civic_moment') {
        await upsertCard(weekIdToSave, {
          card_type: cardType,
          headline: data.headline,
          content: data.content,
          country_id: data.country_id || null,
          event_date: data.event_date || null,
          source_name: data.source_name,
          source_url: data.source_url,
          story_image_url: stripCacheBuster(data.story_image_url) || null,
        });
      } else {
        await upsertCard(weekIdToSave, {
          card_type: cardType,
          content: data.content,
          subtext: data.subtext || null,
        });
      }
    }
  }

  async function ensureWeekId(): Promise<string | null> {
    if (weekData?.week.id) {
      return weekData.week.id;
    }

    const monday = getMondayOfWeek(newWeekOf);
    console.log('[AdminWeeklyCardsCreateEdit] Getting or creating week for:', monday);
    const newWeek = await getOrCreateWeek(monday, 'draft');
    if (!newWeek) {
      console.error('[AdminWeeklyCardsCreateEdit] Failed to get or create week');
      showToast('Failed to get or create week', 'error');
      return null;
    }

    console.log('[AdminWeeklyCardsCreateEdit] Week ready:', newWeek.id);
    setWeekData({
      week: newWeek,
      cards: [],
    });

    return newWeek.id;
  }

  async function handleSaveDraft() {
    setSaving(true);
    try {
      const weekIdToSave = await ensureWeekId();
      if (!weekIdToSave) return;

      await saveCards(weekIdToSave);

      // Reset original form data to current form data
      setOriginalFormData(JSON.parse(JSON.stringify(formData)));

      showToast('Draft saved', 'success');
    } catch (err) {
      console.error('[AdminWeeklyCardsCreateEdit] Error saving draft:', err);
      showToast('Failed to save draft', 'error');
    } finally {
      setSaving(false);
    }
  }

  function validateForPublish(): { valid: boolean; error?: string } {
    if (!formData.artist_wisdom.content || !formData.artist_wisdom.attribution) {
      return { valid: false, error: 'Artist Wisdom: Quote and artist name are required' };
    }
    if (!formData.civic_moment.headline) {
      return { valid: false, error: 'Civic Moment: Headline is required' };
    }
    if (!formData.civic_moment.story_image_url) {
      return { valid: false, error: 'Civic Moment: Story image is required' };
    }
    if (!formData.civic_moment.content) {
      return { valid: false, error: 'Civic Moment: Story is required' };
    }
    if (!formData.civic_moment.country_id) {
      return { valid: false, error: 'Civic Moment: Country is required' };
    }
    if (!formData.civic_moment.event_date) {
      return { valid: false, error: 'Civic Moment: Date is required' };
    }
    if (!formData.civic_moment.source_name) {
      return { valid: false, error: 'Civic Moment: Source name is required' };
    }
    if (!formData.civic_moment.source_url) {
      return { valid: false, error: 'Civic Moment: Source link is required' };
    }
    if (!formData.spark_a_thought.content) {
      return { valid: false, error: 'Spark a Thought: Question is required' };
    }
    return { valid: true };
  }

  async function handlePublishNow() {
    const validation = validateForPublish();
    if (!validation.valid) {
      showToast(validation.error!, 'error');
      return;
    }

    setShowPublishConfirm(false);
    setSaving(true);
    try {
      const weekIdToSave = await ensureWeekId();
      if (!weekIdToSave) return;

      await saveCards(weekIdToSave);

      const success = await publishNow(weekIdToSave);
      if (success) {
        showToast('Published', 'success');
        setTimeout(() => navigateTo('admin-weekly-cards'), 1000);
      } else {
        showToast('Failed to publish', 'error');
      }
    } catch (err) {
      console.error('[AdminWeeklyCardsCreateEdit] Error publishing:', err);
      showToast('Failed to publish', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleSchedule() {
    if (!scheduleDate || !scheduleTime) {
      showToast('Please select date and time', 'error');
      return;
    }

    const validation = validateForPublish();
    if (!validation.valid) {
      showToast(validation.error!, 'error');
      setShowScheduleModal(false);
      return;
    }

    setSaving(true);
    try {
      const weekIdToSave = await ensureWeekId();
      if (!weekIdToSave) return;

      await saveCards(weekIdToSave);
      const scheduledFor = `${scheduleDate}T${scheduleTime}:00`;
      await scheduleWeek(weekIdToSave, scheduledFor);
      setShowScheduleModal(false);
      const dateObj = new Date(scheduledFor);
      showToast(`Scheduled for ${dateObj.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}`, 'success');
    } catch (err) {
      console.error('[AdminWeeklyCardsCreateEdit] Error scheduling:', err);
      showToast('Failed to schedule', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleImageUpload(cardType: CardType, file: File) {
    console.log('[AdminWeeklyCardsCreateEdit] Starting image upload:', { cardType, fileName: file.name, fileSize: file.size, fileType: file.type });

    if (!file.type.startsWith('image/')) {
      showToast('Please select an image file', 'error');
      return;
    }

    if (file.size > 5242880) {
      showToast('Image must be smaller than 5MB', 'error');
      return;
    }

    setUploading(cardType);
    try {
      console.log('[AdminWeeklyCardsCreateEdit] Getting edition ID...');
      const editionId = await ensureWeekId();
      if (!editionId) {
        console.error('[AdminWeeklyCardsCreateEdit] Failed to get edition ID');
        showToast('Failed to create edition', 'error');
        setUploading(null);
        return;
      }
      console.log('[AdminWeeklyCardsCreateEdit] Edition ID:', editionId);

      console.log('[AdminWeeklyCardsCreateEdit] Uploading to Supabase...');
      const imageUrl = await uploadWeeklyCardImage(editionId, cardType, file);

      if (imageUrl) {
        console.log('[AdminWeeklyCardsCreateEdit] Upload successful:', imageUrl);
        if (cardType === 'artist_wisdom') {
          setFormData(prev => ({
            ...prev,
            artist_wisdom: { ...prev.artist_wisdom, image_url: imageUrl },
          }));
        } else if (cardType === 'civic_moment') {
          setFormData(prev => ({
            ...prev,
            civic_moment: { ...prev.civic_moment, story_image_url: imageUrl },
          }));
        }
        showToast('Image uploaded', 'success');
      } else {
        console.error('[AdminWeeklyCardsCreateEdit] Upload returned null');
        showToast('Upload failed - try again', 'error');
      }
    } catch (err) {
      console.error('[AdminWeeklyCardsCreateEdit] Error uploading image:', err);
      showToast(`Upload failed: ${err instanceof Error ? err.message : 'Unknown error'}`, 'error');
    } finally {
      setUploading(null);
    }
  }

  async function handleImageRemove(cardType: CardType) {
    try {
      const editionId = weekData?.week.id;
      if (!editionId) {
        showToast('Edition not found', 'error');
        return;
      }

      await deleteWeeklyCardImage(editionId, cardType);
      if (cardType === 'artist_wisdom') {
        setFormData(prev => ({
          ...prev,
          artist_wisdom: { ...prev.artist_wisdom, image_url: '' },
        }));
      }
      showToast('Image removed', 'success');
    } catch (err) {
      console.error('[AdminWeeklyCardsCreateEdit] Error removing image:', err);
      showToast('Failed to remove image', 'error');
    }
  }

  function showToast(message: string, type: 'success' | 'error') {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  }

  const filteredCountries = countries.filter(c =>
    c.name.toLowerCase().includes(countrySearch.toLowerCase())
  );

  const selectedCountry = countries.find(c => c.id === formData.civic_moment.country_id);

  if (loading) {
    return (
      <AdminShell>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 text-[#40E0D0] animate-spin" />
        </div>
      </AdminShell>
    );
  }

  const pageTitle = isEditMode
    ? `Edit Edition: ${weekData ? formatWeekLabel(weekData.week.week_of) : ''}`
    : 'Create Weekly Edition';

  return (
    <AdminShell>
      <div className="min-h-screen bg-[#111111]">
        {/* Desktop Header */}
        <div className="hidden md:block sticky top-0 z-20 bg-[#111111]/95 backdrop-blur-sm border-b border-[#282828]">
          <div className="max-w-[1120px] px-8 py-4">
            <div className="flex items-center justify-between mb-3">
              <button
                onClick={handleBack}
                className="flex items-center gap-2 text-[#A0A0A0] hover:text-[#F5F5F5] transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-[14px] font-medium">Back to Weekly Cards</span>
              </button>
            </div>

            <div className="flex items-center justify-center">
              <h1 className="text-[20px] font-semibold text-[#F5F5F5]">{pageTitle}</h1>
            </div>

            {isEditMode && weekData && (
              <div className="flex items-center justify-center gap-3 mt-2">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-[11px] font-medium uppercase tracking-wide ${
                  weekData.week.status === 'published'
                    ? 'bg-[#40E0D0]/10 text-[#40E0D0]'
                    : weekData.week.status === 'scheduled'
                    ? 'bg-blue-500/10 text-blue-400'
                    : weekData.week.status === 'archived'
                    ? 'bg-[#808080]/10 text-[#606060]'
                    : 'bg-[#808080]/10 text-[#808080]'
                }`}>
                  {weekData.week.status}
                </span>
                {weekData.week.status === 'scheduled' && weekData.week.publish_at && (
                  <span className="text-[12px] text-[#808080]">
                    Scheduled for {new Date(weekData.week.publish_at).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Mobile Header */}
        <div className="md:hidden sticky top-0 z-20 bg-[#111111]/95 backdrop-blur-sm border-b border-[#282828]">
          <div className="flex items-center justify-between px-4 py-3">
            <button
              onClick={handleBack}
              className="p-2 -ml-2 rounded-lg transition-colors hover:bg-[#1A1A1A] active:bg-[#1A1A1A]"
              aria-label="Back to Weekly Cards"
            >
              <ArrowLeft className="w-5 h-5 text-[#F5F5F5]" />
            </button>

            <h1 className="text-[16px] font-semibold text-[#F5F5F5]">
              {isEditMode ? 'Edit Edition' : 'Create Weekly Edition'}
            </h1>

            <div className="w-9 h-9" />
          </div>

          {isEditMode && weekData && (
            <div className="px-4 pb-3 border-t border-[#282828]/50 pt-3">
              <div className="flex items-center justify-center gap-3">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-[11px] font-medium uppercase tracking-wide ${
                  weekData.week.status === 'published'
                    ? 'bg-[#40E0D0]/10 text-[#40E0D0]'
                    : weekData.week.status === 'scheduled'
                    ? 'bg-blue-500/10 text-blue-400'
                    : weekData.week.status === 'archived'
                    ? 'bg-[#808080]/10 text-[#606060]'
                    : 'bg-[#808080]/10 text-[#808080]'
                }`}>
                  {weekData.week.status}
                </span>
                {weekData.week.status === 'scheduled' && weekData.week.publish_at && (
                  <span className="text-[12px] text-[#808080]">
                    {new Date(weekData.week.publish_at).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                    })}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-4 md:p-8 w-full max-w-[1120px] mx-auto">

        <div className="bg-[#1A1A1A] border border-[#282828] rounded-xl p-8 mb-6">
          <div className="space-y-8">
            <div>
              <h3 className="text-[16px] font-semibold text-[#F5F5F5] mb-4">Artist Wisdom</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-[13px] font-medium text-[#A0A0A0] mb-2">
                    Quote <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    value={formData.artist_wisdom.content}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      artist_wisdom: { ...prev.artist_wisdom, content: e.target.value }
                    }))}
                    rows={3}
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#282828] rounded-lg text-[#F5F5F5] text-[14px] placeholder-[#606060] focus:outline-none focus:border-[#40E0D0] resize-none"
                    placeholder="Enter the artist's quote..."
                  />
                </div>
                <div>
                  <label className="block text-[13px] font-medium text-[#A0A0A0] mb-2">
                    Artist Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.artist_wisdom.attribution}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      artist_wisdom: { ...prev.artist_wisdom, attribution: e.target.value }
                    }))}
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#282828] rounded-lg text-[#F5F5F5] text-[14px] placeholder-[#606060] focus:outline-none focus:border-[#40E0D0]"
                    placeholder="Artist name"
                  />
                </div>
                <div>
                  <label className="block text-[13px] font-medium text-[#A0A0A0] mb-2">
                    Artist Image
                  </label>
                  {formData.artist_wisdom.image_url ? (
                    <div className="flex items-start gap-4">
                      <img
                        src={formData.artist_wisdom.image_url}
                        alt="Artist"
                        className="w-32 h-32 rounded-lg object-cover border border-[#282828]"
                      />
                      <div className="flex flex-col gap-2">
                        <label className="block">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleImageUpload('artist_wisdom', file);
                            }}
                            className="hidden"
                          />
                          <span className="px-4 py-2 bg-[#282828] hover:bg-[#333333] text-[#F5F5F5] rounded-lg text-[13px] font-medium transition-colors cursor-pointer inline-block">
                            Replace Image
                          </span>
                        </label>
                        <button
                          onClick={() => handleImageRemove('artist_wisdom')}
                          className="px-4 py-2 text-[13px] text-red-400 hover:text-red-300 transition-colors text-left"
                        >
                          Remove Image
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          console.log('[AdminWeeklyCardsCreateEdit] File input onChange triggered');
                          console.log('[AdminWeeklyCardsCreateEdit] Files:', e.target.files);
                          const file = e.target.files?.[0];
                          if (file) {
                            console.log('[AdminWeeklyCardsCreateEdit] File selected:', file.name);
                            handleImageUpload('artist_wisdom', file);
                            e.target.value = '';
                          } else {
                            console.log('[AdminWeeklyCardsCreateEdit] No file selected');
                          }
                        }}
                        className="hidden"
                        id="artist-image-upload"
                        disabled={uploading === 'artist_wisdom'}
                      />
                      <label
                        htmlFor="artist-image-upload"
                        onClick={(e) => {
                          console.log('[AdminWeeklyCardsCreateEdit] Label clicked');
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsDraggingArtist(true);
                        }}
                        onDragLeave={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsDraggingArtist(false);
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsDraggingArtist(false);
                          const file = e.dataTransfer.files[0];
                          if (file) {
                            console.log('[AdminWeeklyCardsCreateEdit] File dropped:', file.name);
                            handleImageUpload('artist_wisdom', file);
                          }
                        }}
                        className={`block flex items-center justify-center px-4 py-8 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                          isDraggingArtist
                            ? 'border-[#40E0D0] bg-[#40E0D0]/5'
                            : uploading === 'artist_wisdom'
                            ? 'border-[#40E0D0]'
                            : 'border-[#282828] hover:border-[#40E0D0]'
                        }`}
                      >
                        {uploading === 'artist_wisdom' ? (
                          <div className="text-center">
                            <Loader2 className="w-5 h-5 text-[#40E0D0] animate-spin mx-auto mb-2" />
                            <span className="text-[13px] text-[#808080]">Uploading...</span>
                          </div>
                        ) : (
                          <div className="text-center">
                            <Upload className="w-5 h-5 text-[#808080] mx-auto mb-2" />
                            <span className="text-[13px] text-[#808080]">
                              {isDraggingArtist ? 'Drop image here' : 'Click to upload or drag and drop'}
                            </span>
                          </div>
                        )}
                      </label>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="border-t border-[#282828] pt-8">
              <h3 className="text-[16px] font-semibold text-[#F5F5F5] mb-4">Civic Moment</h3>
              <div className="space-y-4">
                {/* Headline */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-[13px] font-medium text-[#A0A0A0]">
                      Headline <span className="text-red-400">*</span>
                    </label>
                    <span className={`text-[12px] ${
                      formData.civic_moment.headline.length > 50
                        ? 'text-red-400'
                        : 'text-[#606060]'
                    }`}>
                      {formData.civic_moment.headline.length} / 50 chars
                    </span>
                  </div>
                  <input
                    type="text"
                    value={formData.civic_moment.headline}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      civic_moment: { ...prev.civic_moment, headline: e.target.value }
                    }))}
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#282828] rounded-lg text-[#F5F5F5] text-[14px] placeholder-[#606060] focus:outline-none focus:border-[#40E0D0]"
                    placeholder="Short story headline"
                  />
                  {formData.civic_moment.headline.length > 50 && (
                    <p className="text-[12px] text-red-400 mt-1.5">
                      Headline exceeds 50 characters. Please shorten.
                    </p>
                  )}
                </div>

                {/* Story Image */}
                <div>
                  <label className="block text-[13px] font-medium text-[#A0A0A0] mb-2">
                    Story Image <span className="text-red-400">*</span>
                  </label>
                  {formData.civic_moment.story_image_url ? (
                    <div className="flex items-start gap-4">
                      <div className="w-32 h-24 rounded-lg border border-[#282828] bg-[#0A0A0A] flex items-center justify-center overflow-hidden flex-shrink-0">
                        <img
                          src={formData.civic_moment.story_image_url}
                          alt="Story"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex flex-col gap-2">
                        <button
                          type="button"
                          onClick={() => document.getElementById('civic-image-upload')?.click()}
                          disabled={uploading === 'civic_moment'}
                          className="px-4 py-2 bg-[#0A0A0A] border border-[#282828] rounded-lg text-[#F5F5F5] text-[13px] hover:border-[#40E0D0] transition-colors disabled:opacity-50"
                        >
                          {uploading === 'civic_moment' ? 'Uploading...' : 'Replace image'}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setFormData(prev => ({
                              ...prev,
                              civic_moment: { ...prev.civic_moment, story_image_url: '' }
                            }));
                          }}
                          disabled={uploading === 'civic_moment'}
                          className="px-4 py-2 text-red-400 text-[13px] hover:text-red-300 transition-colors disabled:opacity-50"
                        >
                          Remove image
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            handleImageUpload('civic_moment', file);
                          }
                        }}
                        className="hidden"
                        id="civic-image-upload"
                        disabled={uploading === 'civic_moment'}
                      />
                      <label
                        htmlFor="civic-image-upload"
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsDraggingCivic(true);
                        }}
                        onDragLeave={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsDraggingCivic(false);
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsDraggingCivic(false);
                          const file = e.dataTransfer.files[0];
                          if (file) {
                            handleImageUpload('civic_moment', file);
                          }
                        }}
                        className={`block flex items-center justify-center px-4 py-8 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                          isDraggingCivic
                            ? 'border-[#40E0D0] bg-[#40E0D0]/5'
                            : uploading === 'civic_moment'
                            ? 'border-[#40E0D0]'
                            : 'border-[#282828] hover:border-[#40E0D0]'
                        }`}
                      >
                        {uploading === 'civic_moment' ? (
                          <div className="text-center">
                            <Loader2 className="w-5 h-5 text-[#40E0D0] animate-spin mx-auto mb-2" />
                            <span className="text-[13px] text-[#808080]">Uploading...</span>
                          </div>
                        ) : (
                          <div className="text-center">
                            <Upload className="w-5 h-5 text-[#808080] mx-auto mb-2" />
                            <span className="text-[13px] text-[#808080]">
                              {isDraggingCivic ? 'Drop image here' : 'Click to upload or drag and drop'}
                            </span>
                          </div>
                        )}
                      </label>
                    </div>
                  )}
                </div>

                {/* Story */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-[13px] font-medium text-[#A0A0A0]">
                      Story <span className="text-red-400">*</span>
                    </label>
                    <span className={`text-[12px] ${
                      formData.civic_moment.content.length > 200
                        ? 'text-red-400'
                        : 'text-[#606060]'
                    }`}>
                      {formData.civic_moment.content.length} / 200 chars
                    </span>
                  </div>
                  <textarea
                    value={formData.civic_moment.content}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      civic_moment: { ...prev.civic_moment, content: e.target.value }
                    }))}
                    rows={4}
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#282828] rounded-lg text-[#F5F5F5] text-[14px] placeholder-[#606060] focus:outline-none focus:border-[#40E0D0] resize-none"
                    placeholder="Describe the civic moment..."
                  />
                  {formData.civic_moment.content.length > 200 && (
                    <p className="text-[12px] text-red-400 mt-1.5">
                      Story exceeds 200 characters. Please shorten.
                    </p>
                  )}
                </div>

                {/* Country */}
                <div>
                  <label className="block text-[13px] font-medium text-[#A0A0A0] mb-2">
                    Country <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <button
                      onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                      className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#282828] rounded-lg text-left text-[14px] hover:border-[#40E0D0] transition-colors flex items-center justify-between"
                    >
                      <span className={selectedCountry ? 'text-[#F5F5F5]' : 'text-[#606060]'}>
                        {selectedCountry ? `${selectedCountry.name} [${selectedCountry.iso3}]` : 'Select country'}
                      </span>
                      <ChevronDown className="w-4 h-4 text-[#808080]" />
                    </button>
                    {showCountryDropdown && (
                      <div className="absolute z-50 w-full mt-2 bg-[#1A1A1A] border border-[#282828] rounded-lg shadow-xl max-h-64 overflow-hidden">
                        <div className="p-3 border-b border-[#282828]">
                          <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#606060]" />
                            <input
                              type="text"
                              value={countrySearch}
                              onChange={(e) => setCountrySearch(e.target.value)}
                              placeholder="Search countries..."
                              className="w-full pl-10 pr-4 py-2 bg-[#0A0A0A] border border-[#282828] rounded-lg text-[#F5F5F5] text-[14px] placeholder-[#606060] focus:outline-none focus:border-[#40E0D0]"
                            />
                          </div>
                        </div>
                        <div className="overflow-y-auto max-h-80">
                          {filteredCountries.map(country => (
                            <button
                              key={country.id}
                              onClick={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  civic_moment: { ...prev.civic_moment, country_id: country.id }
                                }));
                                setShowCountryDropdown(false);
                                setCountrySearch('');
                              }}
                              className="w-full px-4 py-3 text-left hover:bg-[#282828] transition-colors text-[14px] text-[#F5F5F5] border-b border-[#282828] last:border-b-0"
                            >
                              {country.name} [{country.iso3}]
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Date */}
                <div>
                  <label className="block text-[13px] font-medium text-[#A0A0A0] mb-2">
                    Date <span className="text-red-400">*</span>
                  </label>
                  <CalendarPicker
                    value={formData.civic_moment.event_date}
                    onChange={(date) => setFormData(prev => ({
                      ...prev,
                      civic_moment: { ...prev.civic_moment, event_date: date }
                    }))}
                    placeholder="Select event date"
                  />
                </div>

                {/* Source Name */}
                <div>
                  <label className="block text-[13px] font-medium text-[#A0A0A0] mb-2">
                    Source Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.civic_moment.source_name}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      civic_moment: { ...prev.civic_moment, source_name: e.target.value }
                    }))}
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#282828] rounded-lg text-[#F5F5F5] text-[14px] placeholder-[#606060] focus:outline-none focus:border-[#40E0D0]"
                    placeholder="e.g., Pitchfork"
                  />
                </div>

                {/* Source Link */}
                <div>
                  <label className="block text-[13px] font-medium text-[#A0A0A0] mb-2">
                    Source Link <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="url"
                    value={formData.civic_moment.source_url}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      civic_moment: { ...prev.civic_moment, source_url: e.target.value }
                    }))}
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#282828] rounded-lg text-[#F5F5F5] text-[14px] placeholder-[#606060] focus:outline-none focus:border-[#40E0D0]"
                    placeholder="https://..."
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-[#282828] pt-8">
              <h3 className="text-[16px] font-semibold text-[#F5F5F5] mb-4">Spark a Thought</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-[13px] font-medium text-[#A0A0A0] mb-2">
                    A question that encourages users to reflect on during the week. <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    value={formData.spark_a_thought.content}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      spark_a_thought: { ...prev.spark_a_thought, content: e.target.value }
                    }))}
                    rows={3}
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#282828] rounded-lg text-[#F5F5F5] text-[14px] placeholder-[#606060] focus:outline-none focus:border-[#40E0D0] resize-none"
                    placeholder="Enter a thought-provoking question or reflection..."
                  />
                </div>
                <div>
                  <label className="block text-[13px] font-medium text-[#A0A0A0] mb-2">
                    Subtext (Optional)
                  </label>
                  <input
                    type="text"
                    value={formData.spark_a_thought.subtext}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      spark_a_thought: { ...prev.spark_a_thought, subtext: e.target.value }
                    }))}
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#282828] rounded-lg text-[#F5F5F5] text-[14px] placeholder-[#606060] focus:outline-none focus:border-[#40E0D0]"
                    placeholder="Optional additional context"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Desktop Action Bar */}
        <div className="hidden md:block sticky bottom-6 bg-[#0A0A0A] border border-[#282828] rounded-xl p-4 shadow-2xl">
          <div className="flex items-center justify-end">
            <div className="flex gap-3">
              <button
                onClick={handleSaveDraft}
                disabled={saving}
                className="px-5 py-2.5 bg-[#282828] hover:bg-[#333333] text-[#F5F5F5] rounded-lg text-[14px] font-medium transition-colors disabled:opacity-50"
              >
                {saving ? 'Saving...' : 'Save Draft'}
              </button>
              <button
                onClick={() => {
                  setScheduleDate(weekData?.week.week_of || newWeekOf || '');
                  setScheduleTime('09:00');
                  setShowScheduleModal(true);
                }}
                disabled={saving || !validateForPublish().valid}
                className="px-5 py-2.5 bg-[#282828] hover:bg-[#333333] text-[#F5F5F5] rounded-lg text-[14px] font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                <Calendar className="w-4 h-4" />
                Schedule
              </button>
              <button
                onClick={() => setShowPublishConfirm(true)}
                disabled={saving || !validateForPublish().valid}
                className="px-6 py-2.5 bg-[#40E0D0] hover:bg-[#47EAD8] text-[#0A0A0A] rounded-lg text-[14px] font-semibold transition-all disabled:opacity-50"
              >
                Publish Now
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Bottom Action Bar */}
        <div className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-[#111111]/95 backdrop-blur-sm border-t border-[#282828] pb-safe">
          <div className="px-4 py-3">
            <button
              onClick={() => setShowPublishConfirm(true)}
              disabled={saving || !validateForPublish().valid}
              className="w-full px-4 py-3 bg-[#40E0D0] hover:bg-[#47EAD8] active:bg-[#3DD4C4] rounded-lg text-[#0A0A0A] text-[15px] font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {saving ? 'Publishing...' : 'Publish Now'}
            </button>
            <button
              onClick={() => setShowMobileMenu(true)}
              className="w-full mt-2 py-2 text-[13px] text-[#A0A0A0] hover:text-[#F5F5F5] transition-colors"
            >
              More options
            </button>
          </div>
        </div>

        {/* Add bottom padding on mobile to account for fixed action bar */}
        <div className="md:hidden h-32" />
        </div>
      </div>

      {/* Mobile Overflow Menu */}
      {showMobileMenu && (
        <div className="md:hidden fixed inset-0 z-40 flex items-end" onClick={() => setShowMobileMenu(false)}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div
            className="relative w-full bg-zinc-900/95 border-t border-white/10 rounded-t-2xl shadow-2xl pb-safe"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-4 border-b border-white/10">
              <h3 className="text-[16px] font-semibold text-zinc-100">More Options</h3>
              <button
                onClick={() => setShowMobileMenu(false)}
                className="p-2 -mr-2 rounded-lg hover:bg-white/[0.06] transition-colors"
              >
                <X className="w-5 h-5 text-zinc-400" />
              </button>
            </div>
            <div className="p-4 space-y-2">
              <BottomSheetAction
                label="Save Draft"
                onClick={() => {
                  setShowMobileMenu(false);
                  handleSaveDraft();
                }}
                variant="neutral"
                disabled={saving}
                loading={saving}
              />
              <BottomSheetAction
                label="Schedule"
                onClick={() => {
                  setShowMobileMenu(false);
                  setScheduleDate(weekData?.week.week_of || newWeekOf || '');
                  setScheduleTime('09:00');
                  setShowScheduleModal(true);
                }}
                variant="neutral"
                disabled={saving || !validateForPublish().valid}
              />
            </div>
          </div>
        </div>
      )}

      {showScheduleModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setShowScheduleModal(false)}>
          <div className="bg-[#1A1A1A] border border-[#282828] rounded-xl p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-[18px] font-semibold text-[#F5F5F5] mb-4">Schedule Edition</h3>
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-[13px] font-medium text-[#A0A0A0] mb-2">
                  Publish Date
                </label>
                <CalendarPicker
                  value={scheduleDate}
                  onChange={setScheduleDate}
                  placeholder="Select date"
                />
              </div>
              <div>
                <label className="block text-[13px] font-medium text-[#A0A0A0] mb-2">
                  Publish Time (UTC)
                </label>
                <input
                  type="time"
                  value={scheduleTime}
                  onChange={(e) => setScheduleTime(e.target.value)}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#282828] rounded-lg text-[#F5F5F5] text-[14px] focus:outline-none focus:border-[#40E0D0]"
                />
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowScheduleModal(false)}
                className="flex-1 px-4 py-2.5 bg-[#282828] hover:bg-[#333333] text-[#F5F5F5] rounded-lg font-medium text-[14px] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSchedule}
                disabled={!scheduleDate || !scheduleTime || saving}
                className="flex-1 px-4 py-2.5 bg-[#40E0D0] hover:bg-[#47EAD8] text-[#0A0A0A] rounded-lg font-semibold text-[14px] transition-all disabled:opacity-40"
              >
                Schedule
              </button>
            </div>
          </div>
        </div>
      )}

      {showPublishConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowPublishConfirm(false)}>
          <div className="bg-[#1A1A1A] border border-[#282828] rounded-2xl p-6 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-[20px] font-semibold text-[#F5F5F5] mb-3">Publish now?</h3>
            <p className="text-[14px] text-[#A0A0A0] mb-6 leading-relaxed">
              This will replace the current Active weekly cards immediately. The current Active edition will be moved to Published History.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowPublishConfirm(false)}
                disabled={saving}
                className="px-5 py-2.5 bg-[#282828] hover:bg-[#333333] text-[#F5F5F5] rounded-lg text-[14px] font-medium transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handlePublishNow}
                disabled={saving}
                className="px-5 py-2.5 bg-[#40E0D0] hover:bg-[#47EAD8] text-[#0A0A0A] rounded-lg text-[14px] font-semibold transition-all disabled:opacity-50"
              >
                {saving ? 'Publishing...' : 'Publish now'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Unsaved Changes Dialog */}
      {showUnsavedDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">
          <div className="bg-[#1A1A1A] border border-[#282828] rounded-xl p-6 w-full max-w-md">
            <h3 className="text-[18px] font-semibold text-[#F5F5F5] mb-2">Discard changes?</h3>
            <p className="text-[14px] text-[#A0A0A0] mb-6">
              You have unsaved changes that will be lost.
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={handleContinueEditing}
                className="w-full px-4 py-2.5 bg-[#40E0D0] hover:bg-[#47EAD8] rounded-lg text-[#0A0A0A] text-[14px] font-semibold transition-colors"
              >
                Keep editing
              </button>
              <button
                onClick={handleDiscardChanges}
                className="w-full px-4 py-2.5 bg-[#FF6B6B]/10 border border-[#FF6B6B]/30 hover:bg-[#FF6B6B]/20 rounded-lg text-[#FF6B6B] text-[14px] font-medium transition-colors"
              >
                Discard changes
              </button>
            </div>
          </div>
        </div>
      )}

      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ show: false, message: '', type: 'success' })}
        />
      )}
    </AdminShell>
  );
}
